import { API_URL } from "./api";

export interface DossierSummary {
  id: number;
  titre?: string;
  immatriculation?: string;
  vin?: string;
  client_nom?: string;
  client_prenom?: string;
  statut?: string;
  updated_at?: string;
}

export interface DossierDetail extends DossierSummary {
  notes?: string;
  documents: DossierDocument[];
  valuation?: any;
  cerfa_state?: any;
  ct_data?: any;
}

export interface DossierDocument {
  id: number;
  type: string;
  filename: string;
  url: string;
  created_at?: string;
}

export interface StorageUsage {
  used_bytes: number;
  quota_bytes: number;
  percent: number;
}

export async function listDossiers(params?: { statut?: string; search?: string }) {
  const url = new URL("/dossiers", API_URL);
  if (params?.statut) url.searchParams.set("statut", params.statut);
  if (params?.search) url.searchParams.set("search", params.search);
  const res = await fetch(url.toString(), { credentials: "include" });
  if (!res.ok) throw new Error("Erreur chargement dossiers");
  return (await res.json()) as DossierSummary[];
}

export async function createDossier(payload: Partial<DossierSummary> & { titre?: string }) {
  const res = await fetch(`${API_URL}/dossiers`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Erreur création dossier");
  return (await res.json()) as { id: number };
}

export async function getDossier(id: number) {
  const res = await fetch(`${API_URL}/dossiers/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error("Erreur chargement dossier");
  return (await res.json()) as DossierDetail;
}

export async function updateDossier(id: number, payload: Partial<DossierDetail>) {
  const res = await fetch(`${API_URL}/dossiers/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Erreur mise à jour dossier");
  return res.json();
}

export async function updateDossierCerfaState(id: number, payload: { cerfa_state: any; immatriculation?: string; vin?: string; titre?: string }) {
  const res = await fetch(`${API_URL}/dossiers/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Erreur mise à jour Cerfa");
  return res.json();
}

export async function uploadDossierDocument(id: number, file: File, type: string) {
  const form = new FormData();
  form.append("file", file);
  form.append("type", type);
  const res = await fetch(`${API_URL}/dossiers/${id}/documents`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let msg = "Erreur upload document";
    try {
      const data = await res.json();
      if (data?.detail) msg = data.detail;
    } catch (e) {
      // ignore parsing error, keep generic msg
    }
    throw new Error(msg);
  }
  return (await res.json()) as { id: number; url: string };
}

export async function deleteDossierDocument(dossierId: number, docId: number) {
  const res = await fetch(`${API_URL}/dossiers/${dossierId}/documents/${docId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erreur suppression document");
  return res.json();
}

export async function deleteDossier(dossierId: number) {
  const res = await fetch(`${API_URL}/dossiers/${dossierId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erreur suppression dossier");
  return res.json();
}

export async function getStorageUsage() {
  const res = await fetch(`${API_URL}/storage/usage`, { credentials: "include" });
  if (!res.ok) throw new Error("Erreur quota");
  return (await res.json()) as StorageUsage;
}

export async function archiveDossier(id: number, cleanup = false) {
  const res = await fetch(`${API_URL}/dossiers/${id}/archive`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cleanup }),
  });
  if (!res.ok) throw new Error("Erreur archive dossier");
  return (await res.json()) as { url: string };
}

export async function archiveDossiers(ids: number[], cleanup = false) {
  const res = await fetch(`${API_URL}/dossiers/archive`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, cleanup }),
  });
  if (!res.ok) throw new Error("Erreur archive dossiers");
  return (await res.json()) as { url: string };
}

export async function getDossierValuation(id: number) {
  const res = await fetch(`${API_URL}/dossiers/${id}/valuation`, { credentials: "include" });
  if (!res.ok) throw new Error("Erreur récupération estimation");
  return (await res.json()) as { valuation: any };
}

export async function computeDossierValuation(id: number) {
  const res = await fetch(`${API_URL}/dossiers/${id}/valuation`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erreur calcul estimation");
  return (await res.json()) as { valuation: any };
}

export async function uploadControleTechnique(id: number, file: File) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_URL}/dossiers/${id}/controle-technique`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let msg = "Erreur OCR contrôle technique";
    try {
      const data = await res.json();
      if (data?.detail) msg = data.detail;
    } catch (e) {
      // ignore
    }
    throw new Error(msg);
  }
  return await res.json();
}

export async function deleteControleTechnique(id: number) {
  const res = await fetch(`${API_URL}/dossiers/${id}/controle-technique`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erreur suppression contrôle technique");
  return await res.json();
}
